import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  BookOpen, FileText, CheckCircle2, Settings, Globe, ShieldCheck,
  Printer, Download, Lightbulb,
} from "lucide-react";
import { GUIDE_META, guides, guidesToMarkdown } from "@/data/guides";

const ICONS = { BookOpen, FileText, CheckCircle2, Settings, Globe, ShieldCheck };

function Block({ block }) {
  if (block.type === "p") {
    return <p className="text-sm text-ink leading-relaxed">{block.text}</p>;
  }
  if (block.type === "tip") {
    return (
      <div className="flex gap-2.5 bg-forest-50 border border-forest-100 rounded-lg px-4 py-3">
        <Lightbulb className="h-4 w-4 text-forest-700 shrink-0 mt-0.5" />
        <p className="text-sm text-forest-900 leading-relaxed">{block.text}</p>
      </div>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="space-y-1.5">
        {block.items.map((it, i) => (
          <li key={i} className="text-sm text-ink leading-relaxed flex gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-forest-400 shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function UserGuide() {
  const [downloading, setDownloading] = useState(false);

  const downloadMarkdown = () => {
    setDownloading(true);
    try {
      const blob = new Blob([guidesToMarkdown()], { type: "text/markdown;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sign-off-user-guides.md";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <style>{`@media print {
        header, nav, .no-print { display: none !important; }
        .guide-section { break-inside: avoid; }
        body { background: #fff !important; }
      }`}</style>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2 flex items-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5" /> Help centre
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 tracking-tight">User guides</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-2xl">{GUIDE_META.intro}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 no-print">
          <Button
            variant="outline" onClick={() => window.print()}
            data-testid="print-guide-button"
            className="h-10 border-cream-200 text-forest-800 hover:bg-cream-100"
          >
            <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
          </Button>
          <Button
            onClick={downloadMarkdown} disabled={downloading}
            data-testid="download-guide-button"
            className="h-10 bg-forest-700 hover:bg-forest-800 text-cream-50"
          >
            <Download className="h-4 w-4 mr-1.5" /> Download
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Table of contents */}
        <aside className="lg:col-span-3 no-print">
          <nav className="lg:sticky lg:top-24 space-y-1" data-testid="guide-toc">
            <div className="text-[10px] uppercase tracking-[0.16em] text-ink-soft font-semibold mb-2 px-3">Contents</div>
            {guides.map((g) => {
              const Icon = ICONS[g.icon] || BookOpen;
              return (
                <a
                  key={g.id} href={`#${g.id}`} data-testid={`toc-link-${g.id}`}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-ink-soft hover:text-forest-800 hover:bg-cream-100 transition-colors"
                >
                  <Icon className="h-4 w-4 text-forest-600 shrink-0" />
                  <span>{g.title}</span>
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Guides */}
        <div className="lg:col-span-9 space-y-6">
          {guides.map((g) => {
            const Icon = ICONS[g.icon] || BookOpen;
            return (
              <section
                key={g.id} id={g.id}
                data-testid={`guide-${g.id}`}
                className="guide-section scroll-mt-24 bg-white border border-cream-200 rounded-xl p-6 sm:p-8"
              >
                <div className="flex items-start gap-3 mb-1">
                  <div className="h-9 w-9 rounded-lg bg-forest-50 grid place-items-center shrink-0">
                    <Icon className="h-4.5 w-4.5 text-forest-700" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-forest-900 leading-tight">{g.title}</h2>
                    <p className="text-[11px] uppercase tracking-wider text-ink-mute mt-0.5">For: {g.audience}</p>
                  </div>
                </div>
                <p className="text-sm text-ink-soft mt-3 mb-5">{g.summary}</p>

                {/* Quick start */}
                <div className="bg-cream-100 border border-cream-200 rounded-lg p-5 mb-6">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-forest-800 font-semibold mb-3">Quick start</div>
                  <ol className="space-y-2.5">
                    {g.quickStart.map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm text-ink leading-relaxed">
                        <span className="h-5 w-5 rounded-full bg-forest-700 text-cream-50 text-[11px] font-semibold grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Detailed sections */}
                <div className="space-y-6">
                  {g.sections.map((sec, si) => (
                    <div key={si}>
                      <h3 className="font-heading text-base text-forest-900 mb-2.5">{sec.heading}</h3>
                      <div className="space-y-3">
                        {sec.blocks.map((b, bi) => (
                          <Block key={bi} block={b} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
